/**
 * Renders a scannable QR code for live app surfaces, wrapping the same
 * `qrToSvg` encoder `@mhmo91/schmancy/qr-encode` exports for SSR/Node
 * consumers (static ticket templates, etc.) — see that module for the
 * encoder itself and the default error-correction-level rationale.
 */
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { qrToSvg, type QrErrorCorrectionLevel } from '@schmancy/primitives/qr-encode'
import { css, html, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'

const fixedBox = (pixels: number) => `width: ${pixels}px; height: ${pixels}px`

@schmancyElement('schmancy-qr')
export class SchmancyQr extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-block;
				flex-shrink: 0;
				/*
				 * A code is a square, never a glyph — sitting on the parent's baseline
				 * reserved that font's descender space below it, so the line box ran
				 * a few px taller than the code with the slack always at the bottom,
				 * reading as unexplained padding no property of this component could
				 * remove (the margin prop is the SVG's own quiet zone, symmetric and
				 * inside the viewBox). Inline-level is kept so a code can still sit
				 * beside text; an outer vertical-align declaration still wins over
				 * this one.
				 */
				vertical-align: top;
			}
			/*
			 * size="fill": as wide as the box it is given, square, and never past the
			 * height that box leaves. A box of another shape letterboxes the code, since
			 * the SVG keeps its own aspect; a stretched code is an unscannable one. A
			 * width or height class on the element outranks these.
			 */
			:host([fill]) {
				display: block;
				width: 100%;
				aspect-ratio: 1;
				max-height: 100%;
				flex-shrink: 1;
				min-width: 0;
				min-height: 0;
			}
			:host([fill]) [role='img'],
			:host([fill]) svg {
				display: block;
				width: 100%;
				height: 100%;
			}
		`,
	]

	/** The payload to encode — required; the element renders nothing without it. */
	@property({ type: String }) value = ''

	private fixedOrFill: number | 'fill' = 256

	/**
	 * Rendered width/height in pixels, or `'fill'` for the box the element is given; the SVG's own
	 * `viewBox` stays in module units for crisp scaling. Anything else, a removed attribute included, is the default. @default 256
	 */
	@property({ converter: value => (value === 'fill' ? value : value ? Number(value) : null) })
	get size(): number | 'fill' {
		return this.fixedOrFill
	}
	// The host takes its box the moment this is set, so whatever measures a code that has just been placed reads its real size.
	set size(value: number | 'fill' | null) {
		this.fixedOrFill = value === 'fill' || (typeof value === 'number' && Number.isFinite(value) && value >= 0) ? value : 256
		this.toggleAttribute('fill', value === 'fill')
		// A fixed host takes its box from the rendered one, which would otherwise only follow on the next update.
		if (this.fixedOrFill !== 'fill') this.shadowRoot?.querySelector('[role="img"]')?.setAttribute('style', fixedBox(this.fixedOrFill))
	}

	@property({ type: String, attribute: 'error-correction-level' }) errorCorrectionLevel: QrErrorCorrectionLevel = 'Q'

	/** Quiet-zone width in modules. @default 4 */
	@property({ type: Number }) margin = 4

	@property({ type: String, attribute: 'module-color' }) moduleColor = 'currentColor'

	@property({ type: String, attribute: 'background-color' }) backgroundColor = 'none'

	/**
	 * The accessible name for the `role="img"` this component renders as —
	 * labelable-controls convention: a sensible generic default, overridable
	 * per instance (e.g. an admission ticket names its own code
	 * `label="Admission QR code"`).
	 */
	@property({ type: String }) label = 'QR code'

	render() {
		if (!this.value) return nothing

		const svgMarkup = qrToSvg(this.value, {
			errorCorrectionLevel: this.errorCorrectionLevel,
			margin: this.margin,
			moduleColor: this.moduleColor,
			backgroundColor: this.backgroundColor,
		})

		return html`
			<div role="img" aria-label=${this.label} style=${ifDefined(this.size === 'fill' ? undefined : fixedBox(this.size))}>
				${unsafeSVG(svgMarkup)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-qr': SchmancyQr
	}
}
